import { Capacitor } from '@capacitor/core';
import {
  clearWebUser,
  initFirebaseWeb,
  logWebEvent,
  setWebDefaultEventParameters,
  setWebUser,
} from '@/lib/firebase';
import { hasPrivatePath, redactPrivatePaths } from '@/lib/private-paths';

// process.env.NODE_ENV is string-replaced by Vite at build time (vite.config
// `define`) and is parseable by jest; declare the global so no-undef passes.
// Matches the idiom in firebase.js / lib/tab-navigation.js.
/* global process */

let firebaseAnalytics = null;
let analyticsInitPromise = null;
let desiredIdentity;

// Analytics readiness gate. Both web and native SDKs load through an async
// dynamic import after first paint, so the SDK is NOT ready during the first render —
// which is exactly when a fresh signup's events fire. Any event fired before
// readiness is held in `pendingEvents` and flushed by markAnalyticsReady;
// without this, a native cold start (every fresh signup) drops its funnel
// events (sign_up, tutorial_begin, tutorial_complete) permanently. Readiness is
// also marked when analytics is disabled (dev / unsupported / native build
// without Firebase) so the queue never grows unbounded.
let analyticsReady = false;
let pendingEvents = [];

// GA4's page_title on a page whose path is private (#521). A profile titles
// itself with the person's name and a shared chat with its conversation's
// title, and gtag sends document.title as `dt` on every hit. Fixed and
// untranslated, so these views share one title row in GA4; page_location
// (/app/people/[redacted]) still tells them apart.
export const PRIVATE_PAGE_TITLE = '[redacted]';

// document.title trails a navigation. The next page mounts, and its mount
// effects fire events, before Inertia's `navigate` re-syncs, and <Head>
// applies the new title on a short debounce after that. So a private page
// stays private until a sync sees a public address, and its title stays
// private until it changes.
let onPrivatePage = false;
let lingeringPrivateTitle = null;

function isPrivateLocation(url) {
  try {
    return hasPrivatePath(new URL(url).pathname);
  } catch {
    return true;
  }
}

// The page title GA4 may see for an event fired at `location`. The title on
// screen belongs to the current page or, right after a navigation, to the one
// before it, so both addresses are checked.
export function analyticsPageTitle(
  location = window.location.href,
  title = document.title,
) {
  const isPrivate =
    onPrivatePage ||
    isPrivateLocation(location) ||
    isPrivateLocation(window.location.href) ||
    (lingeringPrivateTitle !== null && title === lingeringPrivateTitle);
  return isPrivate ? PRIVATE_PAGE_TITLE : title;
}

const FLUSHED_IDS_KEY = 'analytics_flushed_event_ids';
const FLUSHED_IDS_MAX = 50;

// Primary dedupe lives in memory; sessionStorage backs it so a reload of a
// page whose HTML still embeds the same analyticsEvents payload (bfcache
// restore, pull-to-refresh) can't re-fire them.
let flushedIds = null;

function loadFlushedIds() {
  if (flushedIds) return flushedIds;
  let stored = [];
  try {
    stored = JSON.parse(sessionStorage.getItem(FLUSHED_IDS_KEY)) || [];
  } catch {
    // unavailable or corrupted — in-memory dedupe still applies
  }
  flushedIds = new Set(stored);
  return flushedIds;
}

function persistFlushedIds() {
  try {
    sessionStorage.setItem(
      FLUSHED_IDS_KEY,
      JSON.stringify([...flushedIds].slice(-FLUSHED_IDS_MAX)),
    );
  } catch {
    // sessionStorage unavailable (private mode) — in-memory only
  }
}

// Fires server-confirmed events (sign_up, login, email_verified, …) delivered
// through the `analyticsEvents` Inertia prop after a PRG redirect — see
// AnalyticsEvents concern + docs/ANALYTICS.md. Each event carries a UUID and
// is fired exactly once per browser session.
export function flushAnalyticsEvents(events) {
  if (!Array.isArray(events) || events.length === 0) return;
  const seen = loadFlushedIds();
  const fresh = events.filter((e) => e?.id && e.name && !seen.has(e.id));
  if (fresh.length === 0) return;
  fresh.forEach((e) => seen.add(e.id));
  persistFlushedIds();
  fresh.forEach((e) => trackEvent(e.name, e.params || {}));
}

// Binds the server-event flush to BOTH Inertia router events. This lives here,
// rather than inline in the entrypoint, so the binding itself is unit-testable —
// it previously wasn't, which is how the same-URL drop went unnoticed in prod.
//
// 'navigate' alone is not enough: @inertiajs/core coerces a visit to the URL
// you're already on into a history *replace* and fires navigate only when
// `!replace`, so events bridged over a same-URL PRG were delivered as props,
// never flushed, and lost when that render consumed the flash. 'success' alone
// is not enough either: it doesn't fire for the initial (non-visit) page load.
// Both together cover every delivery path; flushAnalyticsEvents dedupes per
// event id, so the overlap is harmless.
export function bindAnalyticsFlush(router) {
  const flush = (event) =>
    flushAnalyticsEvents(event?.detail?.page?.props?.analyticsEvents);
  router.on('navigate', flush);
  router.on('success', flush);
}

export function initAnalytics() {
  if (analyticsInitPromise) return analyticsInitPromise;

  analyticsInitPromise = (async () => {
    try {
      // Production analytics is imported only after the first paint. Events
      // and identity updates queue below until this resolves, so deferring the
      // SDK never drops cold-start funnel data.
      if (process.env.NODE_ENV === 'production') {
        if (Capacitor.isNativePlatform()) {
          const { FirebaseAnalytics } =
            await import('@capacitor-firebase/analytics');
          firebaseAnalytics = FirebaseAnalytics;
        } else {
          await initFirebaseWeb({
            pageLocation: stripQuery(window.location.href),
            pageReferrer: stripQuery(document.referrer),
          });
          syncAnalyticsPageContext();
        }
      }
    } finally {
      // Set identity before flushing queued events so a login that completes
      // during the idle import is stitched to those events.
      await applyDesiredIdentity();
      markAnalyticsReady();
    }
  })();

  return analyticsInitPromise;
}

// Marks the active SDK ready and flushes anything queued while it was booting,
// in the order it was queued. Idempotent — the first caller wins.
export function markAnalyticsReady() {
  if (analyticsReady) return;
  analyticsReady = true;
  if (pendingEvents.length === 0) return;
  const queued = pendingEvents;
  pendingEvents = [];
  queued.forEach((e) =>
    sendEvent(e.name, e.params, e.webOnly, e.location, e.title),
  );
}

// Belt-and-braces for the gtag `dl` and `dt` leaks: sendEvent stamps a
// sanitized page_location and page_title on every trackEvent hit, but hits
// that DON'T go through it — GA4's auto-collected events (session_start,
// first_visit, user_engagement) and the setUserId/setUserProperties config
// hits — still carry raw document.location, query string included, and
// document.title, which on a profile is the person's name. Registering
// default event parameters pins a sanitized page_location and page_title on
// every subsequent hit from the SDK. Called at web-analytics boot, on every
// Inertia navigation and again once <Head> has set the new title
// (inertia.jsx), so the pin tracks the SPA page; explicit per-event params
// (page_view's own) still override a default. No-op on native/dev (no web
// SDK).
export function syncAnalyticsPageContext() {
  const location = window.location.href;
  const isPrivate = isPrivateLocation(location);
  if (onPrivatePage && !isPrivate) {
    // Leaving a private page: its title shows until <Head> replaces it.
    lingeringPrivateTitle = document.title;
  } else if (document.title !== lingeringPrivateTitle) {
    lingeringPrivateTitle = null;
  }
  onPrivatePage = isPrivate;
  try {
    setWebDefaultEventParameters({
      page_location: stripQuery(location),
      page_referrer: stripQuery(document.referrer),
      page_title: analyticsPageTitle(location),
    });
  } catch {
    // ignore
  }
}

export async function identifyUser(user) {
  if (!user) return;

  desiredIdentity = user;
  if (!analyticsReady) return;
  await applyDesiredIdentity();
}

async function applyDesiredIdentity() {
  if (desiredIdentity === undefined) return;

  if (desiredIdentity === null) {
    try {
      clearWebUser();
    } catch {
      // ignore
    }
    if (firebaseAnalytics) {
      try {
        await firebaseAnalytics.setUserId({ userId: null });
        for (const key of ['user_plan', 'user_language']) {
          await firebaseAnalytics.setUserProperty({ key, value: null });
        }
      } catch {
        // ignore — Firebase may not be fully configured
      }
    }
    return;
  }

  // Deliberately NOT sending user_doctrine (the user's Christian tradition):
  // religious affiliation is special-category personal data and must not be
  // shared with a third-party analytics provider. Keep it server-side only.
  const properties = {
    user_plan: desiredIdentity.plan || '',
    user_language: desiredIdentity.language || '',
  };

  // Web: Firebase JS SDK
  try {
    setWebUser(String(desiredIdentity.id), properties);
  } catch {
    // ignore
  }

  // Native: Capacitor Firebase plugin
  if (firebaseAnalytics) {
    try {
      await firebaseAnalytics.setUserId({
        userId: String(desiredIdentity.id),
      });
      for (const [key, value] of Object.entries(properties)) {
        await firebaseAnalytics.setUserProperty({ key, value });
      }
    } catch {
      // ignore — Firebase may not be fully configured
    }
  }
}

export async function clearUser() {
  // Clear user-specific localStorage caches and state
  // (preserves device preferences and the anonymous review session marker)
  if (typeof window !== 'undefined') {
    const keysToRemove = [
      'cache:home',
      'cache:daily-readings',
      'bosko:feed-filter',
      'bosko_prayer_timer',
      'prayer_nudge_dismissed',
      'chat-typing-speed',
    ];
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  desiredIdentity = null;
  if (analyticsReady) await applyDesiredIdentity();
}

export function trackEvent(name, params = {}) {
  const payload = params || {};
  // Cold-start guard: on native the Firebase plugin loads via an async dynamic
  // import (initAnalytics), so the SDK is not ready during the first render —
  // exactly when a fresh signup's bridge events (sign_up, tutorial_complete) and
  // client events (tutorial_begin) fire. Sending now would silently no-op and
  // drop them for good, because flushAnalyticsEvents has already marked their
  // ids seen. Queue until initAnalytics resolves, then flush in order on both
  // web and native.
  //
  // A queued event remembers the page it was fired on: the app can navigate
  // before the flush (a native cold start can move on from the first
  // onboarding page), and the flush would otherwise stamp the new page as the
  // event's page_location. Its title is cleaned now, while the current
  // address still describes the page it fired on.
  if (!analyticsReady) {
    pendingEvents.push({
      name,
      params: payload,
      location: window.location.href,
      title: analyticsPageTitle(),
    });
    return;
  }
  sendEvent(name, payload);
}

// Fans one event out to whichever SDK is active. Split from trackEvent so that
// queued events (fired before the SDK finished booting) and live events share
// exactly one send path.
async function sendEvent(
  name,
  params,
  webOnly = false,
  location = window.location.href,
  title = analyticsPageTitle(location),
) {
  // Web: Firebase JS SDK
  try {
    // gtag auto-attaches document.location (dl) — query string included —
    // and document.title (dt) to EVERY event hit, and GA4 records them as
    // the event's page_location and page_title. Our guards on page_view alone
    // don't cover that, so an event fired on /app/bible/search?q=<term> or
    // /app/checkout/return?session_id=<id> would leak the query, and one on a
    // profile the person's name, to GA4 through the back door. Stamping a
    // sanitized page_location and page_title on every event overrides dl and
    // dt per hit; explicit caller params (page_view's own) are sanitized too.
    logWebEvent(name, {
      page_location: stripQuery(location),
      page_title: title,
      ...params,
      ...(params?.page_location && {
        page_location: stripQuery(params.page_location),
      }),
      ...(params?.page_referrer && {
        page_referrer: stripQuery(params.page_referrer),
      }),
      ...(params?.page_title !== undefined && {
        page_title: analyticsPageTitle(location, params.page_title),
      }),
    });
  } catch {
    // ignore
  }

  // Native: Capacitor Firebase plugin
  if (!webOnly && firebaseAnalytics) {
    try {
      await firebaseAnalytics.logEvent({ name, params });
    } catch {
      // ignore
    }
  }
}

// GA4-canonical web page_view. Fires on web only — on native, screen_view
// (via trackEvent) is the equivalent and Firebase's mobile reporting prefers
// it. Inertia navigations should call this in addition to trackEvent so the
// new route lands in GA4's page-based reports.
export function trackPageView({ page_location, page_title } = {}) {
  const location = page_location ?? window.location.href;
  const params = {
    page_location: stripQuery(location),
    // Decided on the raw address: a stripped one (/app/people/[redacted]) no
    // longer reads as private.
    page_title: analyticsPageTitle(location, page_title ?? document.title),
  };
  if (!analyticsReady) {
    pendingEvents.push({ name: 'page_view', params, webOnly: true, location });
    return;
  }
  sendEvent('page_view', params, true, location);
}

// GA4 page_location must never carry the query string: our own routes put
// free text there (Bible search `?q=<term>`) and payment identifiers (Stripe
// `?session_id=cs_…`), and sending window.location.href verbatim would leak
// both to Google — violating the no-free-text/no-PII rule in docs/ANALYTICS.md.
// Reduce to origin + pathname (drop search + hash); GA4's Pages reports are
// path-based, so nothing of analytics value is lost.
export function stripQuery(url) {
  try {
    const u = new URL(url);
    // Private path segments: bearer credentials (the reset page moves its
    // token into the session before rendering; this is the backstop for any
    // old link) and other people's handles on profile, feed-mute and blocked
    // routes in every locale (#521, handle-paths.json).
    return u.origin + redactPrivatePaths(u.pathname);
  } catch {
    return '';
  }
}
